/**
 * ImagePicker — choix d'image pour l'étiquette, la contre-étiquette ou le logo.
 * Trois voies : Prendre une photo (CameraView), Importer un fichier, Rechercher en ligne (IA best-effort).
 * Téléverse vers l'Object Storage via caveApi et remonte le file_id au parent.
 */
import { useState } from 'react';
import { Camera, ImageUp, Search, X, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { caveApi, fileUrl } from '../../services/api';
import CameraView from '../../features/scanner/CameraView';
import { Lightbox } from './Lightbox';

export function ImagePicker({ label, kind, cuveeId, entityId, api, fileId, src, onUploaded, aspect = 'aspect-[3/4]' }) {
  const [mode, setMode] = useState(null); // 'cam' | 'search'
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [zoom, setZoom] = useState(false);
  const [bust, setBust] = useState(0);
  const eid = entityId ?? cuveeId;
  // API par défaut = cuvée (rétrocompatible) ; surchargeable pour les domaines.
  const svc = api || {
    upload: (id, k, file) => caveApi.uploadImage(id, k, file),
    search: (id) => caveApi.searchImages(id),
    fromUrl: (id, k, url) => caveApi.imageFromUrl(id, k, url),
    clear: (id, k) => caveApi.clearImage(id, k),
  };
  // Source d'affichage : URL explicite, sinon fichier stocké (id), sinon URL absolue déjà fournie.
  const isStored = fileId && !src && !String(fileId).startsWith('http');
  const baseSrc = src ?? (fileId ? (String(fileId).startsWith('http') ? fileId : fileUrl(fileId)) : null);
  const resolvedSrc = isStored && bust ? `${baseSrc}&r=${bust}` : baseSrc;

  const rotate = async () => {
    if (!isStored) return;
    setBusy(true);
    try {
      // Rotation côté client (rapide) : on récupère l'image, on la pivote de 90° (sens horaire)
      // sur un canvas, puis on ré-upload (l'upload recompresse en JPEG ≤1600px).
      const blob = await (await fetch(resolvedSrc)).blob();
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.height;        // 90° horaire : on échange largeur/hauteur
      canvas.height = bmp.width;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);          // 90° sens horaire
      ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
      const out = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
      const r = await svc.upload(eid, kind, new File([out], `${kind}.jpg`, { type: 'image/jpeg' }));
      onUploaded?.(r.file_id);
      setBust(Date.now());
    } catch (e) { toast.error(e.message || 'Rotation impossible'); }
    finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    setBusy(true);
    try { const r = await svc.upload(eid, kind, file); onUploaded?.(r.file_id); setMode(null); }
    catch (e) { toast.error(e.message || 'Échec du téléversement'); }
    finally { setBusy(false); }
  };
  const fromCamera = async (b64) => {
    const blob = await (await fetch(`data:image/jpeg;base64,${b64}`)).blob();
    uploadFile(new File([blob], `${kind}.jpg`, { type: 'image/jpeg' }));
  };
  const runSearch = async () => {
    setMode('search'); setBusy(true); setResults([]);
    try { const r = await svc.search(eid); setResults(r.images || []); }
    catch (e) { toast.error(e.message || 'Recherche impossible'); }
    finally { setBusy(false); }
  };
  const pickUrl = async (url) => {
    setBusy(true);
    try { const r = await svc.fromUrl(eid, kind, url); onUploaded?.(r.file_id); setMode(null); }
    catch (e) { toast.error(e.message || 'Image inaccessible'); }
    finally { setBusy(false); }
  };
  const clearPhoto = async () => {
    if (!isStored || busy) return;
    setBusy(true);
    try { await svc.clear?.(eid, kind); onUploaded?.(null); setBust(Date.now()); }
    catch (e) { toast.error(e.message || 'Suppression impossible'); }
    finally { setBusy(false); }
  };

  const toolBtn = 'w-8 h-8 rounded-full flex items-center justify-center text-ink hover:bg-gold100 hover:text-gold800 transition-colors';

  return (
    <div data-testid={`image-picker-${kind}`}>
      <div className={`vd-plate ${aspect} w-full relative group`}>
        {resolvedSrc ? (
          <img src={resolvedSrc} alt={label} className="h-full w-full object-cover rounded cursor-zoom-in" data-testid={`image-${kind}`}
            onDoubleClick={() => setZoom(true)} title="Double-cliquez pour agrandir" />
        ) : (
          <div className="vd-hatch h-full w-full flex items-center justify-center rounded text-[11px] text-n500">{label}</div>
        )}
        {isStored && (
          <button onClick={clearPhoto} disabled={busy}
            className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center bg-paper/90 backdrop-blur-sm border border-hair shadow-sm text-n600 hover:text-red-700 hover:border-red-300 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            data-testid={`img-clear-${kind}`} title="Effacer la photo" aria-label="Effacer la photo">
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        )}
        {/* Barre d'outils centrée en bas, en pastille, inset des bords */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 rounded-full bg-paper/90 backdrop-blur-sm border border-hair shadow-sm px-1 py-1">
          <button onClick={() => setMode('cam')} className={toolBtn} data-testid={`img-photo-${kind}`} title="Prendre une photo"><Camera className="w-4 h-4" strokeWidth={1.6} /></button>
          <label className={`${toolBtn} cursor-pointer`} title="Télécharger une image" data-testid={`img-import-${kind}`}>
            <ImageUp className="w-4 h-4" strokeWidth={1.6} />
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
          </label>
          <button onClick={runSearch} className={toolBtn} data-testid={`img-search-${kind}`} title="Rechercher en ligne"><Search className="w-4 h-4" strokeWidth={1.6} /></button>
          {isStored && (
            <button onClick={rotate} className={toolBtn} data-testid={`img-rotate-${kind}`} title="Pivoter la photo" disabled={busy}><RotateCw className="w-4 h-4" strokeWidth={1.6} /></button>
          )}
        </div>
      </div>

      {mode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`image-modal-${kind}`}>
          <div className="absolute inset-0 bg-ink/40" onClick={() => setMode(null)} />
          <div className="relative bg-paper rounded-lg shadow-lg w-full max-w-md p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="kicker">{mode === 'cam' ? 'Prendre une photo' : 'Rechercher en ligne'} · {label}</p>
              <button onClick={() => setMode(null)} aria-label="Fermer"><X className="w-5 h-5" /></button>
            </div>
            {mode === 'cam' && <CameraView onResult={fromCamera} captureLabel="Utiliser cette photo" compact />}
            {mode === 'search' && (
              <div>
                {busy && <p className="text-[13px] text-n600">Recherche…</p>}
                {!busy && results.length === 0 && <p className="text-[13px] text-n500">Aucune image trouvée. Utilisez « Prendre une photo » ou « Importer ».</p>}
                <div className="grid grid-cols-3 gap-2">
                  {results.map((r, i) => (
                    <button key={i} onClick={() => pickUrl(r.url)} className="vd-plate aspect-square overflow-hidden" data-testid={`img-result-${i}`} disabled={busy}>
                      <img src={r.url} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {zoom && resolvedSrc && <Lightbox src={resolvedSrc} alt={label} onClose={() => setZoom(false)} />}
    </div>
  );
}
